/**
 * Token types for C language subset
 */
export enum TokenType {
  // Keywords
  FOR = 'FOR',
  WHILE = 'WHILE',
  DO = 'DO',
  IF = 'IF',
  ELSE = 'ELSE',
  INT = 'INT',
  FLOAT = 'FLOAT',
  CHAR = 'CHAR',
  RETURN = 'RETURN',
  BREAK = 'BREAK',
  CONTINUE = 'CONTINUE',

  // Operators
  ASSIGN = 'ASSIGN',           // =
  PLUS = 'PLUS',               // +
  MINUS = 'MINUS',             // -
  MULTIPLY = 'MULTIPLY',       // *
  DIVIDE = 'DIVIDE',           // /
  MODULO = 'MODULO',           // %
  INCREMENT = 'INCREMENT',     // ++
  DECREMENT = 'DECREMENT',     // --
  PLUS_ASSIGN = 'PLUS_ASSIGN', // +=
  MINUS_ASSIGN = 'MINUS_ASSIGN', // -=

  // Comparison operators
  EQUAL = 'EQUAL',             // ==
  NOT_EQUAL = 'NOT_EQUAL',     // !=
  LESS_THAN = 'LESS_THAN',     // <
  GREATER_THAN = 'GREATER_THAN', // >
  LESS_EQUAL = 'LESS_EQUAL',   // <=
  GREATER_EQUAL = 'GREATER_EQUAL', // >=

  // Logical operators
  AND = 'AND',                 // &&
  OR = 'OR',                   // ||
  NOT = 'NOT',                 // !

  // Delimiters
  SEMICOLON = 'SEMICOLON',     // ;
  COMMA = 'COMMA',             // ,
  LPAREN = 'LPAREN',           // (
  RPAREN = 'RPAREN',           // )
  LBRACE = 'LBRACE',           // {
  RBRACE = 'RBRACE',           // }

  // Literals and identifiers
  NUMBER = 'NUMBER',
  STRING = 'STRING',
  CHAR_LITERAL = 'CHAR_LITERAL',
  IDENTIFIER = 'IDENTIFIER',

  // Special
  EOF = 'EOF',
  NEWLINE = 'NEWLINE',
  COMMENT = 'COMMENT',
}

export interface Token {
  type: TokenType
  value: string
  line: number
  column: number
}

export class Tokenizer {
  private input: string
  private position: number = 0
  private line: number = 1
  private column: number = 1
  private tokens: Token[] = []

  private keywords: Map<string, TokenType> = new Map([
    ['for', TokenType.FOR],
    ['while', TokenType.WHILE],
    ['do', TokenType.DO],
    ['if', TokenType.IF],
    ['else', TokenType.ELSE],
    ['int', TokenType.INT],
    ['float', TokenType.FLOAT],
    ['char', TokenType.CHAR],
    ['return', TokenType.RETURN],
    ['break', TokenType.BREAK],
    ['continue', TokenType.CONTINUE],
  ])

  constructor(input: string) {
    this.input = input
  }

  tokenize(): Token[] {
    while (this.position < this.input.length) {
      this.skipWhitespace()
      
      if (this.position >= this.input.length) {
        break
      }

      const token = this.nextToken()
      if (token && token.type !== TokenType.COMMENT) {
        this.tokens.push(token)
      }
    }

    this.tokens.push({
      type: TokenType.EOF,
      value: '',
      line: this.line,
      column: this.column,
    })

    return this.tokens
  }

  private nextToken(): Token | null {
    const ch = this.current()
    const line = this.line
    const column = this.column

    // Single-line comment
    if (ch === '/' && this.peek() === '/') {
      return this.skipSingleLineComment()
    }

    // Multi-line comment
    if (ch === '/' && this.peek() === '*') {
      return this.skipMultiLineComment()
    }

    // Numbers
    if (this.isDigit(ch)) {
      return this.readNumber()
    }

    // Identifiers and keywords
    if (this.isAlpha(ch) || ch === '_') {
      return this.readIdentifier()
    }

    // String literals
    if (ch === '"') {
      return this.readString()
    }

    // Character literals
    if (ch === "'") {
      return this.readCharLiteral()
    }

    // Two-character operators
    const twoChar = ch + this.peek()
    const twoCharToken = this.getTwoCharOperator(twoChar, line, column)
    if (twoCharToken) {
      this.advance()
      this.advance()
      return twoCharToken
    }

    // Single-character operators and delimiters
    const singleCharToken = this.getSingleCharToken(ch, line, column)
    if (singleCharToken) {
      this.advance()
      return singleCharToken
    }

    // Unknown character
    this.advance()
    return null
  }

  private current(): string {
    return this.input[this.position] || ''
  }

  private peek(offset: number = 1): string {
    return this.input[this.position + offset] || ''
  }

  private advance(): void {
    if (this.current() === '\n') {
      this.line++
      this.column = 1
    } else {
      this.column++
    }
    this.position++
  }

  private skipWhitespace(): void {
    while (this.position < this.input.length && /\s/.test(this.current())) {
      this.advance()
    }
  }

  private skipSingleLineComment(): Token {
    const line = this.line
    const column = this.column
    let value = ''
    
    // Skip //
    this.advance()
    this.advance()
    
    while (this.position < this.input.length && this.current() !== '\n') {
      value += this.current()
      this.advance()
    }
    
    return {
      type: TokenType.COMMENT,
      value: value.trim(),
      line,
      column,
    }
  }

  private skipMultiLineComment(): Token {
    const line = this.line
    const column = this.column
    let value = ''
    
    // Skip /*
    this.advance()
    this.advance()
    
    while (this.position < this.input.length) {
      if (this.current() === '*' && this.peek() === '/') {
        // Skip */
        this.advance()
        this.advance()
        break
      }
      value += this.current()
      this.advance()
    }
    
    return {
      type: TokenType.COMMENT,
      value: value.trim(),
      line,
      column,
    }
  }

  private isDigit(ch: string): boolean {
    return /[0-9]/.test(ch)
  }

  private isAlpha(ch: string): boolean {
    return /[a-zA-Z]/.test(ch)
  }

  private isAlphaNumeric(ch: string): boolean {
    return /[a-zA-Z0-9_]/.test(ch)
  }

  private readNumber(): Token {
    const line = this.line
    const column = this.column
    let value = ''
    
    while (this.isDigit(this.current())) {
      value += this.current()
      this.advance()
    }
    
    // Handle floating point numbers
    if (this.current() === '.' && this.isDigit(this.peek())) {
      value += this.current()
      this.advance()
      
      while (this.isDigit(this.current())) {
        value += this.current()
        this.advance()
      }
    }
    
    return {
      type: TokenType.NUMBER,
      value,
      line,
      column,
    }
  }

  private readIdentifier(): Token {
    const line = this.line
    const column = this.column
    let value = ''
    
    while (this.isAlphaNumeric(this.current())) {
      value += this.current()
      this.advance()
    }
    
    // Check if it's a keyword
    const keywordType = this.keywords.get(value)
    
    return {
      type: keywordType || TokenType.IDENTIFIER,
      value,
      line,
      column,
    }
  }

  private readString(): Token {
    const line = this.line
    const column = this.column
    let value = ''
    
    // Skip opening quote
    this.advance()
    
    while (this.position < this.input.length && this.current() !== '"') {
      if (this.current() === '\\') {
        this.advance()
        // Handle escape sequences
        const escaped = this.current()
        switch (escaped) {
          case 'n': value += '\n'; break
          case 't': value += '\t'; break
          case 'r': value += '\r'; break
          case '\\': value += '\\'; break
          case '"': value += '"'; break
          default: value += escaped
        }
      } else {
        value += this.current()
      }
      this.advance()
    }
    
    // Skip closing quote
    this.advance()
    
    return {
      type: TokenType.STRING,
      value,
      line,
      column,
    }
  }

  private readCharLiteral(): Token {
    const line = this.line
    const column = this.column
    let value = ''
    
    // Skip opening quote
    this.advance()
    
    if (this.current() === '\\') {
      this.advance()
      // Handle escape sequences
      const escaped = this.current()
      switch (escaped) {
        case 'n': value = '\n'; break
        case 't': value = '\t'; break
        case 'r': value = '\r'; break
        case '\\': value = '\\'; break
        case "'": value = "'"; break
        default: value = escaped
      }
      this.advance()
    } else {
      value = this.current()
      this.advance()
    }
    
    // Skip closing quote
    if (this.current() === "'") {
      this.advance()
    }
    
    return {
      type: TokenType.CHAR_LITERAL,
      value,
      line,
      column,
    }
  }

  private getTwoCharOperator(twoChar: string, line: number, column: number): Token | null {
    const operators: Record<string, TokenType> = {
      '++': TokenType.INCREMENT,
      '--': TokenType.DECREMENT,
      '+=': TokenType.PLUS_ASSIGN,
      '-=': TokenType.MINUS_ASSIGN,
      '==': TokenType.EQUAL,
      '!=': TokenType.NOT_EQUAL,
      '<=': TokenType.LESS_EQUAL,
      '>=': TokenType.GREATER_EQUAL,
      '&&': TokenType.AND,
      '||': TokenType.OR,
    }
    
    const type = operators[twoChar]
    if (type) {
      return { type, value: twoChar, line, column }
    }
    
    return null
  }

  private getSingleCharToken(ch: string, line: number, column: number): Token | null {
    const tokens: Record<string, TokenType> = {
      '=': TokenType.ASSIGN,
      '+': TokenType.PLUS,
      '-': TokenType.MINUS,
      '*': TokenType.MULTIPLY,
      '/': TokenType.DIVIDE,
      '%': TokenType.MODULO,
      '<': TokenType.LESS_THAN,
      '>': TokenType.GREATER_THAN,
      '!': TokenType.NOT,
      ';': TokenType.SEMICOLON,
      ',': TokenType.COMMA,
      '(': TokenType.LPAREN,
      ')': TokenType.RPAREN,
      '{': TokenType.LBRACE,
      '}': TokenType.RBRACE,
    }
    
    const type = tokens[ch]
    if (type) {
      return { type, value: ch, line, column }
    }
    
    return null
  }
}